import { useState, useCallback } from 'react';
import { products, productCategories } from '@/mocks/products';
import useSEO from '@/hooks/useSEO';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function ProductCard({ product }: { product: (typeof products)[0] }) {
  const [imgError, setImgError] = useState(false);
  const { t } = useTranslation('common');

  return (
    <div className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:shadow-md transition-all duration-300 hover:-translate-y-1">
      <a
        href={product.url}
        target="_blank"
        rel="noopener nofollow"
        className="block relative"
      >
        <div className="aspect-square bg-background-100 overflow-hidden relative">
          {!imgError ? (
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-background-200">
              <div className="w-14 h-14 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-3xl text-secondary-500" />
              </div>
            </div>
          )}
          <div className="absolute top-3 left-3">
            <span className="inline-block px-2.5 py-1 text-xs font-medium bg-secondary-100 text-secondary-900 rounded-md">
              {product.category}
            </span>
          </div>
        </div>
      </a>

      <div className="p-4 md:p-5">
        <a
          href={product.url}
          target="_blank"
          rel="noopener nofollow"
          className="block"
        >
          <h3 className="text-sm font-medium text-foreground-900 leading-snug line-clamp-2 min-h-[2.8rem] hover:text-primary-600 transition-colors">
            {product.name}
          </h3>
        </a>

        <div className="mt-3 flex items-center justify-between">
          <div>
            <span className="text-lg font-bold text-primary-600">¥{product.priceCNY}</span>
            <span className="ml-2 text-xs text-foreground-600">≈ ${product.priceUSD}</span>
          </div>
        </div>

        <a
          href={product.url}
          target="_blank"
          rel="noopener nofollow"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-shopping-cart-line" />
          {t('buyNow')}
        </a>
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const { t } = useTranslation('common');
  const [activeCategory, setActiveCategory] = useState('All');

  useSEO({
    title: 'All Products – AllChinaBuy Spreadsheet',
    description: 'Browse our curated product catalog from AllChinaBuy spreadsheet. Shoes, hoodies, t-shirts, accessories, bags and more. Click any product to buy now.',
    canonical: '/products',
    ogTitle: 'All Products – AllChinaBuy Spreadsheet',
    ogDesc: 'Browse 100+ curated products from AllChinaBuy spreadsheet. Click to buy on the main website.',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'AllChinaBuy Spreadsheet Products',
      numberOfItems: products.length,
      itemListElement: products.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: p.name,
        url: p.url,
      })),
    },
  });

  const filteredProducts = activeCategory === 'All'
    ? products
    : products.filter((p) => p.category === activeCategory);

  const categoryCounts = productCategories.reduce(
    (acc, cat) => {
      acc[cat.name] = products.filter((p) => p.category === cat.name).length;
      return acc;
    },
    {} as Record<string, number>
  );

  const handleCategoryClick = useCallback((category: string) => {
    setActiveCategory(category);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <nav aria-label="breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-600">
              <li>
                <a href="/" className="hover:text-primary-600 transition-colors">{t('breadcrumbHome')}</a>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs" />
              </li>
              <li className="text-foreground-900 font-medium">{t('allProducts')}</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              {t('productCatalog')}
            </h1>
            <p className="text-base md:text-lg text-foreground-700">
              {t('productCatalogDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-sm border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => handleCategoryClick('All')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeCategory === 'All'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              All ({products.length})
            </button>
            {productCategories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat.name
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name} ({categoryCounts[cat.name] || 0})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground-950">
              {activeCategory === 'All' ? t('allProducts') : activeCategory}
            </h2>
            <span className="text-sm text-foreground-600">
              {filteredProducts.length} {t('items')}
            </span>
          </div>

          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                <i className="ri-shopping-bag-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-600">{t('noProducts')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Main Store CTA */}
      <section className="bg-primary-50 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            {t('lookingForMore')}
          </h2>
          <p className="text-foreground-700 mb-6 max-w-xl mx-auto">
            {t('productCatalogDesc')}
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-md font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-external-link-line" />
            {t('visitFullStore')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}